<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { formatNumberSemiTruncate } from '@/helpers/utils'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import { usePlayer } from '@/composables/game/player'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import {
    PATHWAY_EVENT_STEP_COST_TYPE,
    type PathwayEvent,
    type Step,
} from '@/types/shared/pathway-event'
import { formatDuration } from '@/helpers/date'
import { isiOS } from '@/helpers/browser'
import PathwayEventStep from '@/components/PathwayEventStep.vue'
import { useWatchOverlays } from '@/composables/helpers/scoll'
import { useOutOfSpecialCurrency, usePathwayEvent } from '@/composables/game/pathway-events'
import PathwayEventPurchaseStepModal from '@/components/modals/PathwayEventPurchaseStepModal.vue'
import { useConfig } from '@/composables/game/config'
import CoinConfettiImage from '@/assets/images/icons/gold-coin-icon-s3.webp'
import SpinsConfettiImage from '@/assets/images/fortune-wheel.webp'
import TicketConfettiImage from '@/assets/images/emojis/emoji-ticket.png'
import StarsConfettiImage from '@/assets/images/premium.png'
import { useConfetti } from '@/composables/game/confetti'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { GameSound, useSounds } from '@/composables/game/sounds'
import ExplodeEmojisAnimation from '@/components/ExplodeEmojisAnimation.vue'
import api from '@/services/api'
import { useToaster } from '@/composables/game/toasters'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'

import { useGiveaway } from '@/composables/game/giveaway'
import isScreenSmallerThan from '@/composables/helpers/is-screen-smaller-than'
import CrossCircleIcon from '@/components/icons/CrossCircleIcon.vue'
import OutOfSpecialCurrencyModal from '@/components/modals/OutOfSpecialCurrencyModal.vue'

const props = defineProps<{
	show: boolean
	pathwayEvent: PathwayEvent
}>()

const emit = defineEmits<{
	(e: 'hide'): void
}>()

const { topNumber, bottomNumber } = useFullScreenDimensions()
const { coins, updatePlayer, player } = usePlayer()
const { syncConfig } = useConfig()
const { availableSpins, updateState: updateFortuneWheelState } = useFortuneWheel()
const { countdownForEventEnd, playerProgress, hasFinishedCurrentProgress } = usePathwayEvent(props.pathwayEvent)
const { showConfetti } = useConfetti()
const { addHapticFeedback } = useHapticFeedback()
const { playSound } = useSounds()
const { notify } = useToaster()
const {
  playerTickets,
  syncGiveaway,
} = useGiveaway()
const {
  selectedSpecialCurrencyForModal,
  canShowOutOfSpecialCurrencyModal,
  getAvailableEcommerceItems,
  closeOutOfSpecialCurrencyModal,
} = useOutOfSpecialCurrency()

const scale = ref(1)
const stepsContainer = ref<HTMLElement | null>(null)
const pillsContainer = ref<HTMLElement | null>(null)
const selectedStep = ref<Step & { index: number; originalIndex: number } | null>(null)
const showClaimStepModal = ref(false)
const loadingStepOriginalIndex = ref<null | number>(null)
const isSmallOrMediumPhone = isScreenSmallerThan(428)

const bannerStyle = computed(() => {
  return { background: `linear-gradient(90deg, ${props.pathwayEvent.mainColor} 0%, #1C1C1E 100%)` }
})

const showCoinBalance = computed(() => Object.values(props.pathwayEvent.config.steps).some(step => !!step.reward.goldenCoins))
const showTicketBalance = computed(() => Object.values(props.pathwayEvent.config.steps).some(step => !!step.reward.giveawayTickets))
const showSpinBalance = computed(() => Object.values(props.pathwayEvent.config.steps).some(step => !!step.reward.spins))
const showStarBalance = computed(() => Object.values(props.pathwayEvent.config.steps).some(step => !!step.reward.dropeeStars))

function showSpecialCurrencyBalance(currency: string) {
  return Object.values(props.pathwayEvent.config.steps).some(step => !!(step.reward.specialCurrencies as Record<string, number> | undefined)?.[currency] || (step.cost.type === PATHWAY_EVENT_STEP_COST_TYPE.SPECIAL_CURRENCY && step.cost.currencyId === currency))
}

const scaleOverlay = () => {
  nextTick(() => {
    if (!isSmallOrMediumPhone.value && (pillsContainer.value?.offsetHeight ?? 0) <= 32) {
      scale.value = 1
      return
    }

    if (stepsContainer.value) {
      const gridRect = stepsContainer.value.getBoundingClientRect()
      const availableHeight = window.innerHeight - gridRect.top - bottomNumber.value
      const gridHeight = stepsContainer.value.scrollHeight
      scale.value = gridHeight > availableHeight
        ? availableHeight / gridHeight
        : 1
    }
  })
}

const goldCircleAnimationItems = ref<Record<string, boolean>>({
  goldenCoins: false,
  spins: false,
  dropeeStars: false,
  giveawayTickets: false,
  ...Object.fromEntries(
    Object.keys(allSpecialCurrenciesConfig.value).map(id => [id, false]),
  ),
})

useWatchOverlays(computed(() => props.show))

const unsortedSteps = computed(() => props.pathwayEvent.config.steps)
const sortedSteps = computed((): (Step & { originalIndex: number })[] => {
  const stepsArray = Object.entries(unsortedSteps.value)
    .sort((a, b) => Number(b[0]) - Number(a[0]))
    .map(([key, step]) => ({
      ...step,
      originalIndex: Number(key),
    }))

  const result: (Step & { originalIndex: number })[] = []

  const firstGroupSize = 4
  const firstGroup = stepsArray.slice(0, firstGroupSize)
  result.push(...firstGroup)

  let groupIndex = 1
  for (let i = firstGroupSize; i < stepsArray.length; i += 3) {
    const group = stepsArray.slice(i, i + 3)
    if (groupIndex % 2 === 1) {
      group.reverse()
    }
    result.push(...group)
    groupIndex++
  }

  return result
})

function arrowPositionClass(index: number) {
  const noTranslateY = ' translate-y-0'
  const topArrowClass = '-top-[30px] left-1/2 translate-x-[calc(-50%+1px)] -rotate-[90deg]' + noTranslateY
  const leftArrowClass = '-left-[40px] rotate-[180deg]'

  switch (index) {
  case 0:
    return '-top-[2px] left-4' + noTranslateY
  case 1:
    return 'w-[48px] left-4 -top-[80px] translate-x-0' + noTranslateY
  case 2:
    return leftArrowClass
  case 3:
    return leftArrowClass
  case 6:
    return topArrowClass
  case 7:
    return topArrowClass
  case 8:
    return leftArrowClass
  case 9:
    return leftArrowClass
  case 12:
    return topArrowClass
  default:
    return ''
  }
}

function isStepDone(index: number) {
  const originalIndex = sortedSteps.value[index].originalIndex - 1
  return playerProgress.value.currentStep > originalIndex
}

function isStepClaimable(index: number) {
  const originalIndex = sortedSteps.value[index].originalIndex - 1
  return !isStepDone(index) && playerProgress.value.currentStep === originalIndex
}

function closeClaimStepModal() {
  showClaimStepModal.value = false
  setTimeout(() => selectedStep.value = null, 100)
}

async function onPayForPathway(eventId: string, step: Step & { originalIndex: number }) {
  loadingStepOriginalIndex.value = step.originalIndex
  try {
    await api.payForPathwayEvent({ eventId, stepNumber: step.originalIndex })
    await onSuccessfulClaim(step)
  } catch (e) {
    notify({ type: 'error', title: 'Failed to claim' })
  } finally {
    loadingStepOriginalIndex.value = null
  }
}

function onStepPurchase(step: Step & { originalIndex: number }, index: number) {
  selectedStep.value = { ...step, index }
}

function showGoldenCircles(reward: Record<string, number | Partial<Record<string, number>>>) {
  Object.keys(goldCircleAnimationItems.value).forEach(key => {
    goldCircleAnimationItems.value[key] = false
  })

  setTimeout(() => {
    Object.keys(reward).forEach(rewardType => {
      let key = ''
      const isSpecialCurrency = rewardType === 'specialCurrencies'
      if (isSpecialCurrency) {
        const specialCurrencies = reward.specialCurrencies as Record<string, number>
        if (specialCurrencies) {
          key = Object.keys(specialCurrencies).find(key => specialCurrencies[key]) ?? ''
        }
      } else {
        key = rewardType
      }
      goldCircleAnimationItems.value[key] = true
    })
  }, 100)
}

async function onSuccessfulClaim(step: Step & { originalIndex: number }) {
  addHapticFeedback('medium')
  showGoldenCircles(step.reward)
  showConfetti({ ...getConfettiShapes(step) })
  const { playerStats } = await syncConfig()
  updatePlayer(playerStats)
  if (hasFinishedCurrentProgress.value) {
    playSound(GameSound.PATHWAY_COMPLETED)
  }
  await updateFortuneWheelState()
  await syncGiveaway()
}

const rewardShapeImageMap = computed<Record<string, string>>(() => ({
  spins: SpinsConfettiImage,
  goldenCoins: CoinConfettiImage,
  giveawayTickets: TicketConfettiImage,
  dropeeStars: StarsConfettiImage,
  ...Object.fromEntries(
    Object.entries(allSpecialCurrenciesConfig.value).map(([key, value]) => [key, value.image]),
  ),
}))

function getConfettiImages(step: Step) {
  const images: { src: string, width: number, height: number, particles: { size: { value: number } } }[] = []

  Object.keys(step.reward).forEach(rewardType => {
    let imageKey = ''
    const isSpecialCurrency = rewardType === 'specialCurrencies'
    if (isSpecialCurrency) {
      const specialCurrencies = step.reward.specialCurrencies
      if (specialCurrencies) {
        imageKey = Object.keys(specialCurrencies).find(key => (specialCurrencies as Record<string, number>)[key]) ?? ''
      }
    } else {
      imageKey = rewardType
    }
    images.push({
      'src': rewardShapeImageMap.value[imageKey],
      'width': 24,
      'height': 24,
      'particles': {
        'size': {
          'value': 12,
        },
      },
    })
  })
  return images
}

function getConfettiShapes(step: Step) {
  return {
    count: 100,
    decay: 0.9,
    shapes: ['image'],
    shapeOptions: {
      'image': getConfettiImages(step),
    },
  }
}

function onOutOfSpecialCurrency(currencyId: string) {
  if (canShowOutOfSpecialCurrencyModal(currencyId)) {
    selectedSpecialCurrencyForModal.value = currencyId
    return
  }
  notify({
    type: 'error',
    title: `Not enough <img width="20px" height="20px" style="display: inline-block; vertical-align: middle;" src="${rewardShapeImageMap.value[currencyId]}" alt="Icon">`,
    // description: h('span', { class: 'font-medium' }, [
    //   h('span', {
    //     class: 'underline text-accent1-default',
    //     onClick: () => {
    //       router.push({ name: RouteName.REWARDS })
    //       emit('hide')
    //     },
    //   }, 'Spin the wheel'),
    //   ' to get more now!',
    // ]),
  })
}

watch(selectedStep, selectedStep => {
  if (!selectedStep) return
  setTimeout(() => showClaimStepModal.value = true, 100)
})

watch(stepsContainer, () => {
  scaleOverlay()
})

onMounted(async () => {
  await syncGiveaway()
  window.addEventListener('resize', scaleOverlay)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', scaleOverlay)
})
</script>
<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      class="fixed max-h-screen h-screen w-screen left-0 bottom-0 bg-dark-most-bottom-layer/[.66]  backdrop-blur-[4px] flex flex-col z-[800] px-4 normal-zeros"
      :style="{
        paddingTop: `${topNumber + 24}px`,
      }"
    >
      <div
        class="h-full w-full max-w-[440px] mx-auto flex-1 flex flex-col"
      >
        <div
          class="flex items-start z-[1] gap-x-4"
        >
          <div
            ref="pillsContainer"
            class="flex-1 flex items-center gap-1 flex-wrap"
          >
            <template
              v-for="specialCurrency in Object.keys(allSpecialCurrenciesConfig)"
              :key="specialCurrency"
            >
              <div
                v-if="showSpecialCurrencyBalance(specialCurrency)"
                class="balance-pill scale-in"
              >
                <div class="w-5 h-5 min-w-[20px] relative flex justify-center">
                  <img
                    class="object-cover w-auto h-5"
                    :src="rewardShapeImageMap[specialCurrency]"
                  >
                  <ExplodeEmojisAnimation
                    :show="goldCircleAnimationItems[specialCurrency]"
                    :image-src="undefined"
                    :loop="2"
                  />
                </div>

                <span>{{ formatNumberSemiTruncate((player.specialCurrencies as Record<string, number>)[specialCurrency] ?? 0) }}</span>
              </div>
            </template>
            <div
              v-if="showCoinBalance"
              class="balance-pill scale-in"
            >
              <div class="w-5 h-5 min-w-[20px] relative">
                <img
                  :src="CoinIcon"
                  class="w-full h-full"
                >
                <ExplodeEmojisAnimation
                  :show="goldCircleAnimationItems.goldenCoins"
                  :image-src="undefined"
                  :loop="2"
                />
              </div>
              <span>{{ formatNumberSemiTruncate(coins) }}</span>
            </div>
            <div
              v-if="showSpinBalance"
              class="balance-pill scale-in"
            >
              <div class="w-5 h-5 min-w-[20px] relative">
                <img
                  class="object-cover w-full h-full"
                  src="@/assets/images/fortune-wheel.webp"
                >
                <ExplodeEmojisAnimation
                  :show="goldCircleAnimationItems.spins"
                  :image-src="undefined"
                  :loop="2"
                />
              </div>
              <span>{{ formatNumberSemiTruncate(availableSpins) }}</span>
            </div>
            <div
              v-if="showStarBalance"
              class="balance-pill scale-in"
            >
              <div class="w-5 h-5 min-w-[20px] relative">
                <img
                  class="object-cover size-5 min-w-[20px]"
                  src="@/assets/images/premium.png"
                >
                <ExplodeEmojisAnimation
                  :show="goldCircleAnimationItems.dropeeStars"
                  :image-src="undefined"
                  :loop="2"
                />
              </div>
              <span>{{ formatNumberSemiTruncate(player.ecommerce.starsBalance) }}</span>
            </div>
            <div
              v-if="showTicketBalance"
              class="balance-pill scale-in"
            >
              <div class="w-5 h-5 min-w-[20px] relative">
                <img
                  class="w-5 h-5 min-w-[20px] object-cover"
                  src="@/assets/images/emojis/emoji-ticket.png"
                >
                <ExplodeEmojisAnimation
                  :show="goldCircleAnimationItems.giveawayTickets"
                  :image-src="undefined"
                  :loop="2"
                />
              </div>
              <span>{{ formatNumberSemiTruncate(playerTickets) }}</span>
            </div>
          </div>
          <CrossCircleIcon
            class="size-8 min-w-[32px] text-white/[87%]"
            @click="addHapticFeedback('light'); emit('hide')"
          />
        </div>

        <div
          ref="stepsContainer"
          class="w-full h-full flex flex-col"
          :style="{
            transform: `scale(${scale})`,
            transformOrigin: 'top center'
          }"
        >
          <div
            class="pt-4 w-full flex-1 flex flex-col items-center"
          >
            <div
              class="w-full relative flex flex-col items-center justify-end h-[124px]"
            >
              <img
                class="w-full h-[400px] absolute top-[-80px] left-0 right-0 pointer-events-none"
                src="@/assets/images/blur-gold-ellipse.png"
              >
              <img
                class="absolute top-[-46px] left-1/2 -translate-x-1/2 w-[294px] h-auto object-cover"
                src="@/assets/images/confetti.png"
              >
              <img
                class="absolute -bottom-[10px] left-1/2 -translate-x-1/2 h-[143px] w-auto object-cover"
                :src="pathwayEvent.mainIcon"
              >
              <div
                class="z-[1] text-center text-[36px] leading-[36px] font-bold text-white black-stroke"
                :class="{'ios': isiOS()}"
              >
                {{ pathwayEvent.eventName }}
              </div>
            </div>
            <div
              class="mt-4 h-8 -mx-8 z-[1] shadow-banner flex items-center justify-center"
              :class="scale < 1 ? 'w-[300%]' : 'w-[calc(100%+64px)]'"
              :style="bannerStyle"
            >
              <span class="mr-3 black-stroke uppercase font-bold text-[20px] leading-6 banner-ends-in">Ends in</span>
              <span
                class="golden-stroke-black-text font-bold text-[20px] leading-6 uppercase shadow-countdown px-px min-w-[95px]"
              >
                {{
                  formatDuration(countdownForEventEnd)
                }}
              </span>
            </div>
            <div
              class="mt-6 w-full grid grid-cols-3 gap-y-4 gap-x-8"
            >
              <PathwayEventStep
                v-for="(step, index) in sortedSteps"
                :key="index"
                :event-id="pathwayEvent.id"
                :original-index="step.originalIndex"
                :class="{'col-span-3 min-w-[218px] w-max justify-self-center': index === 0}"
                :step="step"
                :main-color="pathwayEvent.mainColor"
                :multi-reward-support="index === 0"
                :is-done="isStepDone(index)"
                :is-claimable="isStepClaimable(index)"
                :has-arrow="index > 0"
                :is-loading="loadingStepOriginalIndex === step.originalIndex"
                :arrow-class="`${arrowPositionClass(index)}`"
                @on-pay="onPayForPathway(pathwayEvent.id, step)"
                @on-purchase="onStepPurchase(step, index)"
                @on-claim-done="onSuccessfulClaim(step)"
                @out-of-special-currency="onOutOfSpecialCurrency"
              >
                <template
                  v-if="isStepClaimable(index) || index === 1"
                  #arrowIcon
                >
                  <template v-if="isStepClaimable(index)">
                    <img
                      v-if="index === 1"
                      class="object-cover w-full h-full"
                      src="@/assets/images/pathway-top-up-arrow-green.png"
                    >
                    <img
                      v-else
                      class="object-cover w-full h-full rotate-[180deg]"
                      src="@/assets/images/pathway-double-arrows.png"
                    >
                  </template>
                  <img
                    v-else
                    class="object-cover w-full h-full"
                    src="@/assets/images/pathway-top-up-arrow.png"
                  >
                </template>
              </PathwayEventStep>
            </div>
          </div>
        </div>
      </div>

      <PathwayEventPurchaseStepModal
        v-if="selectedStep && showClaimStepModal"
        :show="showClaimStepModal"
        :step="selectedStep"
        :pathway-event="pathwayEvent"
        @purchase-done="onSuccessfulClaim(selectedStep)"
        @hide="closeClaimStepModal"
      />
    </div>
  </Transition>

  <OutOfSpecialCurrencyModal
    v-if="selectedSpecialCurrencyForModal"
    :show="!!selectedSpecialCurrencyForModal"
    :currency="selectedSpecialCurrencyForModal"
    :ecommerce-items="getAvailableEcommerceItems(selectedSpecialCurrencyForModal)"
    @hide="closeOutOfSpecialCurrencyModal($event)"
  />
</template>

<style scoped lang="scss">
@import '@/assets/styles/sales.scss';

.balance-pill {
	@apply py-1.5 pl-2.5 pr-3 rounded-full bg-dark-top-most-layer flex items-center gap-x-1.5 h-8 text-sm font-semibold -tracking-[0.28px] min-w-max;
}

.bg-golden-blur {
	background: linear-gradient(269deg, rgba(245, 194, 80, 0.88) -31.87%, rgba(255, 255, 159, 0.88) 65.88%, rgba(255, 205, 88, 0.88) 97.27%);
}

.black-stroke {
	-webkit-text-stroke-width: 5px;
	-webkit-text-stroke-color: #000;
	text-shadow: 0px 0px 5px rgba(0, 0, 0, 0.95),
	0px 0px 10px rgba(0, 0, 0, 0.85),
	0px 0px 15px rgba(0, 0, 0, 0.75),
	0px 0px 20px rgba(0, 0, 0, 0.65);
	paint-order: stroke fill;

	&.ios {
		text-shadow: 0px 0px 5px rgba(0, 0, 0, 0.55) !important;
	}
}

.banner-ends-in {
	text-shadow: 0px 0px 115.874px rgba(0, 0, 0, 0.50);
}

.shadow-banner {
	filter: drop-shadow(0px 8px 12px rgba(0, 0, 0, 0.52));
}

.shadow-countdown {
	filter: drop-shadow(0px 0px 12px #000);
}

</style>